import { useEffect, useState, useRef, useCallback } from 'react';
import { Upload, Trash2, Copy, CheckCircle2, Image, Music, Video, Search, Library, X, Download, Eye } from 'lucide-react';
import { defaultArticles, defaultProducts, defaultPages } from '../api/defaults';

interface UsageMap { [fileUrl: string]: string[]; }

interface MediaFile {
  name: string; url: string; type: 'image' | 'video' | 'audio'; size: number;
  original: string; uploadedAt: string; label?: string;
}

type FilterType = 'all' | 'image' | 'video' | 'audio';

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(2) + ' MB';
}

function FileTypeIcon({ type }: { type: string }) {
  if (type === 'video') return <Video size={20} className="text-purple-400" />;
  if (type === 'audio') return <Music size={20} className="text-green-400" />;
  return <Image size={20} className="text-[#4ab8ff]" />;
}

function MediaCard({ file, onDelete, onCopy, copiedUrl, usages }: {
  file: MediaFile; onDelete: (f: MediaFile) => void;
  onCopy: (url: string) => void; copiedUrl: string | null;
  usages?: string[];
}) {
  const [showPreview, setShowPreview] = useState(false);
  const isCopied = copiedUrl === file.url;

  return (
    <div className="group bg-[#07090f] border border-white/10 rounded-xl overflow-hidden hover:border-white/25 transition-all">
      {/* Thumbnail */}
      <div className="aspect-video bg-[#0a0c12] relative flex items-center justify-center overflow-hidden">
        {file.type === 'image' ? (
          <img src={file.url} alt={file.original}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : file.type === 'video' ? (
          <video src={file.url} className="w-full h-full object-cover" preload="metadata" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/20">
            <Music size={32} />
            <span className="text-xs">{file.original.split('.').pop()?.toUpperCase()}</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {file.type === 'image' && (
            <button onClick={() => setShowPreview(true)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Preview">
              <Eye size={15} />
            </button>
          )}
          <button onClick={() => onCopy(file.url)}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Copiază URL">
            {isCopied ? <CheckCircle2 size={15} className="text-green-400" /> : <Copy size={15} />}
          </button>
          <a href={file.url} download={file.original}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Descarcă">
            <Download size={15} />
          </a>
          <button onClick={() => onDelete(file)}
            className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 transition-colors" title="Șterge">
            <Trash2 size={15} />
          </button>
        </div>

        {/* Type badge */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
            file.type === 'image' ? 'bg-[#295CA8]/80 text-[#4ab8ff]' :
            file.type === 'video' ? 'bg-purple-500/80 text-purple-200' : 'bg-green-500/80 text-green-200'
          }`}>
            {file.type.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-xs text-white/70 font-medium truncate" title={file.original}>{file.original}</p>

        {/* URL intern — vizibil clar */}
        <div className="mt-1.5 flex items-center gap-1 bg-[#0a0c12] border border-[#295CA8]/25 rounded px-2 py-1">
          <span className="text-[9px] text-[#4ab8ff]/50 font-mono flex-shrink-0">🔗</span>
          <span className="text-[9px] text-[#4ab8ff]/70 font-mono truncate flex-1" title={file.url}>
            {file.url}
          </span>
        </div>

        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-white/30">{formatSize(file.size)}</span>
          <span className="text-[10px] text-white/25">{file.uploadedAt.split('T')[0]}</span>
        </div>

        {/* Utilizare — unde e folosita imaginea */}
        {usages && usages.length > 0 && (
          <div className="mt-2 space-y-0.5">
            <p className="text-[9px] text-white/30 uppercase tracking-wider mb-1">Folosită în:</p>
            {usages.slice(0, 4).map((u, i) => (
              <div key={i} className="flex items-start gap-1 text-[9px] text-green-400/80 bg-green-500/8 border border-green-500/15 rounded px-1.5 py-0.5" title={u}>
                <span className="flex-shrink-0 mt-0.5">{u.startsWith('📦') ? '📦' : u.startsWith('📰') ? '📰' : u.startsWith('🖥') ? '🖥️' : '📍'}</span>
                <span className="truncate">{u.replace(/^[📦📰🖥️]+\s*/,'')}</span>
              </div>
            ))}
            {usages.length > 4 && <div className="text-[9px] text-white/25 pl-1">+{usages.length - 4} alte locuri</div>}
          </div>
        )}
        {usages && usages.length === 0 && (
          <div className="mt-2 flex items-center gap-1 text-[9px] text-white/20 italic bg-white/3 rounded px-1.5 py-1">
            <span>⚪</span> Nefolosită în site
          </div>
        )}
        <button onClick={() => onCopy(file.url)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[10px] text-white/30 hover:text-[#4ab8ff] transition-colors py-1 rounded border border-white/5 hover:border-[#295CA8]/30">
          {isCopied ? <><CheckCircle2 size={10} className="text-green-400" /> URL copiat!</> : <><Copy size={10} /> Copiază URL intern</>}
        </button>
      </div>

      {/* Full preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85" onClick={() => setShowPreview(false)}>
          <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowPreview(false)}
              className="absolute -top-10 right-0 text-white/60 hover:text-white text-2xl leading-none">×</button>
            <img src={file.url} alt={file.original}
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
            <p className="text-center text-white/40 text-xs mt-2">{file.original} — {formatSize(file.size)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MediaLibraryPage() {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [usageMap, setUsageMap] = useState<UsageMap>({});

  // Extrage URL-uri din HTML (src="...")
  const extractSrcUrls = (html: string): string[] => {
    const matches = html.match(/src=["']([^"']+)["']/g) || [];
    return matches.map(m => m.replace(/src=["']/,'').replace(/["']/,''));
  };

  // Normalizeaza un URL de imagine pentru matching (accepta atat /php-backend/cms-data/media/ cat si orice path relativ)
  const normalizeUrl = (url: string): string | null => {
    if (!url) return null;
    // URL deja in formatul nostru intern
    if (url.includes('/php-backend/cms-data/media/')) return url;
    // URL relativ simplu (ex: /images/foto.jpg) — nu e din media library
    return null;
  };

  const buildUsageMap = useCallback((products: unknown[], articles: unknown[], pages: unknown[]) => {
    const map: UsageMap = {};
    const addUsage = (url: string, label: string) => {
      const normalized = normalizeUrl(url);
      if (!normalized) return;
      if (!map[normalized]) map[normalized] = [];
      if (!map[normalized].includes(label)) map[normalized].push(label);
    };

    // Merge produse PHP + default static
    const allProds = [...(products as Array<{name:string;image?:string;description?:string}>), ...defaultProducts];
    const seenProds = new Set<string>();
    allProds.forEach(p => {
      if (seenProds.has(p.name)) return; seenProds.add(p.name);
      addUsage(p.image || '', `📦 Produs: ${p.name}`);
      extractSrcUrls(p.description || '').forEach(u => addUsage(u, `📦 Desc. produs: ${p.name}`));
    });

    // Merge articole PHP + default static
    const allArts = [...(articles as Array<{title:string;image?:string;content?:string;titleRo?:string}>), ...defaultArticles];
    const seenArts = new Set<string>();
    allArts.forEach(a => {
      const key = (a as any).titleRo || a.title || '';
      if (seenArts.has(key)) return; seenArts.add(key);
      addUsage((a as any).image || '', `📰 Blog: ${(a as any).titleRo || a.title}`);
      extractSrcUrls((a as any).content || (a as any).contentRo || '').forEach(u =>
        addUsage(u, `📰 Blog conținut: ${(a as any).titleRo || a.title}`)
      );
    });

    // Merge pagini PHP + default static
    const allPages = [...(pages as Array<{pageName:string;sections?:Array<{roText?:string;imageUrl?:string}>}>), ...defaultPages];
    const seenPages = new Set<string>();
    allPages.forEach(pg => {
      if (seenPages.has(pg.pageName)) return; seenPages.add(pg.pageName);
      (pg.sections || []).forEach(s => {
        addUsage(s.imageUrl || '', `🖥️ Pagina: ${pg.pageName}`);
        extractSrcUrls(s.roText || '').forEach(u => addUsage(u, `🖥️ Pagina: ${pg.pageName}`));
      });
    });

    setUsageMap(map);
  }, []);

  // Converteste un item din media_index.json in MediaFile
  const toMediaFile = (item: Record<string, unknown>): MediaFile => {
    const name = String(item.name || '');
    const url = String(item.url || `/php-backend/cms-data/media/${name}`);
    const ext = name.split('.').pop()?.toLowerCase() || '';
    const type: 'image'|'video'|'audio' = ['mp4','webm','mov'].includes(ext) ? 'video'
      : ['mp3','wav','ogg'].includes(ext) ? 'audio' : 'image';
    return {
      name,
      url,
      type,
      size: Number(item.size || 0),
      original: name,
      uploadedAt: String(item.uploadedAt || new Date().toISOString()),
      label: String(item.label || name),
    };
  };

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      // Incearca PHP API mai intai; daca esueaza foloseste media_index.json static
      let mediaFiles: MediaFile[] = [];
      try {
        const r = await fetch('/php-backend/cms-api/media.php');
        if (r.ok) {
          const data = await r.json();
          if (Array.isArray(data.files) && data.files.length > 0) {
            mediaFiles = data.files;
          } else {
            throw new Error('empty');
          }
        } else {
          throw new Error('not ok');
        }
      } catch {
        // Fallback la media_index.json static (pre-seeded)
        try {
          const r2 = await fetch('/php-backend/cms-data/media_index.json');
          if (r2.ok) {
            const idx = await r2.json();
            const items: Record<string,unknown>[] = Array.isArray(idx) ? idx : (idx.media || idx.files || []);
            mediaFiles = items.map(toMediaFile);
          }
        } catch { mediaFiles = []; }
      }
      setFiles(mediaFiles);

      // Incarca usage map din surse
      const [prods, blog, pages] = await Promise.all([
        fetch('/php-backend/cms-api/products.php').then(r => r.json()).catch(() => ({ products: [] as unknown[] })),
        fetch('/php-backend/cms-api/blog.php').then(r => r.json()).catch(() => ({ articles: [] as unknown[] })),
        fetch('/php-backend/cms-api/pages.php').then(r => r.json()).catch(() => ({ pages: [] as unknown[] })),
      ]);
      buildUsageMap(
        Array.isArray(prods) ? prods : (prods.products || []),
        Array.isArray(blog) ? blog : (blog.articles || []),
        Array.isArray(pages) ? pages : (pages.pages || [])
      );
    } finally {
      setLoading(false);
    }
  }, [buildUsageMap]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const doUpload = async (fileList: File[]) => {
    if (fileList.length === 0) return;
    setUploading(true);
    let done = 0;
    for (const file of fileList) {
      setUploadProgress(`Se procesează: ${file.name} (${done + 1}/${fileList.length})`);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await fetch('/php-backend/cms-api/media.php', { method: 'POST', body: fd });
        const d = await r.json();
        if (d.file) setFiles(f => [d.file, ...f]);
      } catch { /* skip failed */ }
      done++;
    }
    setUploading(false);
    setUploadProgress('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    if (list.length > 0) doUpload(list);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const list = Array.from(e.dataTransfer.files);
    if (list.length > 0) doUpload(list);
  };

  const handleDelete = async (file: MediaFile) => {
    if (!confirm(`Ștergi "${file.original}"?`)) return;
    await fetch('/php-backend/cms-api/media.php', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: file.name }),
    });
    setFiles(f => f.filter(x => x.name !== file.name));
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  // Filtered
  const filtered = files.filter(f => {
    const matchType = filterType === 'all' || f.type === filterType;
    const matchSearch = !search || f.original.toLowerCase().includes(search.toLowerCase()) || f.name.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const counts = {
    all: files.length,
    image: files.filter(f => f.type === 'image').length,
    video: files.filter(f => f.type === 'video').length,
    audio: files.filter(f => f.type === 'audio').length,
  };

  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Library size={22} /> Librărie Media
          </h1>
          <p className="text-white/40 text-sm mt-1">
            {files.length} fișiere · {formatSize(totalSize)} total · imagini convertite automat în WebP
          </p>
        </div>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Upload size={16} /> {uploading ? uploadProgress || 'Se procesează...' : 'Încarcă fișiere'}
        </button>
        <input ref={fileInputRef} type="file" multiple
          accept="image/*,video/*,audio/*,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.webm,.mov,.mp3,.wav,.aac"
          className="hidden" onChange={handleFileChange} />
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-3 rounded-xl text-xs"
        style={{ background: 'rgba(74,184,255,0.06)', border: '1px solid rgba(74,184,255,0.12)' }}>
        <span className="text-[#4ab8ff] text-base">ℹ</span>
        <div className="text-white/50">
          <strong className="text-white/70">Formate acceptate:</strong> Imagini (JPG, PNG, GIF, WebP, SVG) · Video (MP4, WebM, MOV) · Audio (MP3, WAV, AAC, OGG).
          Imaginile JPG/PNG sunt <strong className="text-white/70">convertite automat în WebP</strong> la încărcare pentru performanță optimă.
          <strong className="text-yellow-400/80 ml-1">Folosiți doar imagini pentru care dețineți dreptul legal de utilizare.</strong>
        </div>
      </div>

      {/* Drag & Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl py-6 text-center transition-all cursor-pointer ${
          dragOver ? 'border-[#4ab8ff] bg-[#295CA8]/10' : 'border-white/10 hover:border-white/20'
        }`}
        onClick={() => fileInputRef.current?.click()}>
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin w-8 h-8 border-2 border-[#4ab8ff] border-t-transparent rounded-full" />
            <p className="text-sm text-white/60">{uploadProgress || 'Se procesează...'}</p>
          </div>
        ) : (
          <div>
            <Upload size={24} className={`mx-auto mb-2 ${dragOver ? 'text-[#4ab8ff]' : 'text-white/20'}`} />
            <p className="text-sm text-white/40">{dragOver ? 'Eliberați pentru a încărca' : 'Drag & Drop sau click pentru a încărca'}</p>
            <p className="text-xs text-white/20 mt-1">Multiple fișiere simultan · WebP auto-conversie</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Type tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
          {([
            { k: 'all', label: 'Toate', icon: null },
            { k: 'image', label: 'Imagini', icon: <Image size={12} /> },
            { k: 'video', label: 'Video', icon: <Video size={12} /> },
            { k: 'audio', label: 'Audio', icon: <Music size={12} /> },
          ] as const).map(({ k, label, icon }) => (
            <button key={k} onClick={() => setFilterType(k as FilterType)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${filterType === k ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
              {icon}{label}
              <span className={`ml-1 px-1 py-0.5 rounded-full text-[9px] ${filterType === k ? 'bg-white/20' : 'bg-white/10'}`}>
                {counts[k as FilterType]}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Caută fișiere..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              <X size={12} />
            </button>
          )}
        </div>

        {filtered.length !== files.length && (
          <span className="text-xs text-white/30">{filtered.length} din {files.length} afișate</span>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-video bg-white/5 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center">
          <Library size={48} className="mx-auto text-white/10 mb-4" />
          <p className="text-white/30 text-sm">{files.length === 0 ? 'Librăria este goală. Încarcă primul fișier!' : 'Niciun fișier găsit pentru filtrele selectate.'}</p>
          {search && <button onClick={() => setSearch('')} className="mt-2 text-xs text-[#4ab8ff] hover:underline">Resetează căutarea</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((f, i) => (
            <MediaCard key={f.name + i} file={f} onDelete={handleDelete} onCopy={handleCopy} copiedUrl={copiedUrl} usages={usageMap[f.url]} />
          ))}
        </div>
      )}
    </div>
  );
}
